import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api, { searchUsers } from '@/lib/api';
import { DEPT_FULL_NAMES as DEPT_NAMES, canSeeFinancials, canPrintJobCard } from '@/lib/constants';
import {
  ArrowLeft, Save, FileText, Settings2, Printer,
  RefreshCw, Scissors, Plus, Trash2, ExternalLink, QrCode, ChevronUp, ChevronDown,
} from 'lucide-react';
import { downloadJobCardQr } from '@/lib/jobCardQr';
import { openAuthedFile } from '@/lib/fileDownload';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { isPlyFieldVisible } from '@/components/DynamicFieldRenderer';
import JobHeaderBand from './JobCard/JobHeaderBand';
import PhoneJobCardHeader from './JobCard/PhoneJobCardHeader';
import { useIsMobile } from '@/hooks/useIsMobile';
import ProcessTable from './JobCard/ProcessTable';
import PhoneProcessCards from './JobCard/PhoneProcessCards';
import { ActionRow, Card as PhoneCard } from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

import { Screen } from '@/components/data';

import { STATUS_COLORS } from '@/lib/status-colors';

export default function JobCardPage() {
  const { jobCardId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [jobCard, setJobCard] = useState(null);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [productConfig, setProductConfig] = useState(null);
  const [specs, setSpecs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialSpecs, setInitialSpecs] = useState({});

  // Split Into Types state
  const [splitEligibility, setSplitEligibility] = useState(null);
  const [splitTypes, setSplitTypes] = useState([]);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [newTypes, setNewTypes] = useState([{ name: '', qty: '' }, { name: '', qty: '' }]);
  const [splitting, setSplitting] = useState(false);
  const [addTypeDialogOpen, setAddTypeDialogOpen] = useState(false);
  const [addTypeName, setAddTypeName] = useState('');
  const [addTypeQty, setAddTypeQty] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);

  // Real-time pipeline preview state
  const [pipelinePreview, setPipelinePreview] = useState({ stages: [] });
  const [processTable, setProcessTable] = useState({ rows: [], totals: {}, job_info: {} });
  const [pipelineBusy, setPipelineBusy] = useState(false);
  const [stageFlow, setStageFlow] = useState(null);

  const [designers, setDesigners] = useState([]);

  // Moving the job between stages is the same call, the same payload and the
  // same two roles the order screen uses — a job card is moved from wherever the
  // person who has to move it happens to be standing.
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveBackTarget, setMoveBackTarget] = useState(null);
  const [moveBackReason, setMoveBackReason] = useState('');

  const canEditPipeline = ['admin', 'supervisor', 'designer'].some(
    role => (user?.roles || [user?.role]).includes(role)
  );

  const canMoveStages = ['admin', 'supervisor'].includes(user?.role);

  const isAdmin = (user?.roles || [user?.role]).includes('admin');

  const showFinancials = canSeeFinancials(user);

  // the roles /job-card/:id/fill admits — this page is open to everyone, so the
  // button has to match the route or half the office lands on a bounce
  const canFillCard = ['admin', 'supervisor', 'designer', 'receptionist'].some(
    role => (user?.roles || [user?.role]).includes(role)
  );

  // Editing the pipeline is a supervisor's call, so it posts and reloads rather
  // than keeping a local copy — the server is the one that decides whether a
  // stage that has been worked on may move.
  const editPipeline = async (request, message) => {
    setPipelineBusy(true);
    try {
      await request();
      toast.success(message);
      await Promise.all([loadProcessTable(), loadJobCard()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change the pipeline');
    }
    setPipelineBusy(false);
  };

  const jcId = jobCard?.id || jobCardId;

  const moveStage = (index, direction) => {
    const keys = processTable.rows.map(r => r.stage_key);
    const target = index + direction;
    if (target < 0 || target >= keys.length) return;
    [keys[index], keys[target]] = [keys[target], keys[index]];
    return editPipeline(
      () => api.post(`/job-cards/${jcId}/stages/reorder`, { stage_order: keys }),
      'Pipeline reordered',
    );
  };

  const removeStage = (row) => editPipeline(
    () => api.post(`/job-cards/${jcId}/remove-stage`, { stage_key: row.stage_key }),
    `${row.process} removed`,
  );

  // Insert-only: the stage goes where it is asked for, and goods already past
  // that point of the line keep going rather than being fetched back for it.
  const addStage = (stageKey, afterStageKey) => editPipeline(
    () => api.post(`/job-cards/${jcId}/stages/add`, {
      stage_key: stageKey,
      after_stage_key: afterStageKey,
    }),
    'Process added',
  );

  // Hoisted out of the effect it used to live in: splitting a job card into
  // types, adding a type and deleting one all call this to refresh, and from
  // inside the effect it was out of their scope — the API call succeeded and
  // then the handler threw before the page could reload.
  const loadJobCard = useCallback(async () => {
    try {
      let res;
      if (jobCardId) {
        res = await api.get(`/job-cards/${jobCardId}`);
      } else {
        const orderId = searchParams.get('orderId');
        const lineItemId = searchParams.get('lineItemId');
        if (orderId && lineItemId) {
          res = await api.get(`/job-cards/by-line-item/${orderId}/${lineItemId}`);
        }
      }
      if (res?.data) {
        setJobCard(res.data);
        const currentSpecs = res.data.specifications || {};
        setSpecs(currentSpecs);
        setInitialSpecs(currentSpecs);
        setFieldDefs(res.data.product_config?.job_card_fields || []);
        setProductConfig(res.data.product_config || null);
        setHasChanges(false);
      }
    } catch (err) {
      toast.error('Failed to load job card');
    }
    setLoading(false);
  }, [jobCardId, searchParams]);

  useEffect(() => { loadJobCard(); }, [loadJobCard]);

  const handlePrintJobCard = async () => {
    const jcId = jobCard?.id || jobCardId;
    if (!jcId) return;
    try {
      await openAuthedFile(`/jobcard/card/${jcId}/pdf`);
    } catch {
      toast.error('Could not open the job card');
    }
  };

  const loadSplitData = async () => {
    const jcId = jobCard?.id || jobCardId;
    if (!jcId) return;
    try {
      const [eligRes, typesRes] = await Promise.all([
        api.get(`/job-cards/${jcId}/split-eligibility`),
        api.get(`/job-cards/${jcId}/split-types`),
      ]);
      setSplitEligibility(eligRes.data);
      setSplitTypes(typesRes.data.types || []);
    } catch {
      // Not critical
    }
  };

  useEffect(() => { loadSplitData(); }, [jobCard?.id, jobCardId]);

  // The process table is built server-side from what the batches actually did,
  // so the machine bookings, the scanned times and the three time buckets all
  // come from one place rather than being reassembled in the browser.
  const loadProcessTable = useCallback(async () => {
    const jcId = jobCard?.id || jobCardId;
    if (!jcId) return;
    try {
      const res = await api.get(`/production/runs/process-table/${jcId}`);
      setProcessTable(res.data);
    } catch {
      // an older job card with no pipeline yet — the table shows its own message
    }
  }, [jobCard?.id, jobCardId]);

  useEffect(() => { loadProcessTable(); }, [loadProcessTable]);

  // The movement trail is a record for the office, not something the floor acts
  // on, so it is loaded (and rendered) for admins only.
  const loadStageFlow = useCallback(async () => {
    const jcId = jobCard?.id || jobCardId;
    if (!jcId || !isAdmin) return;
    try {
      const res = await api.get(`/job-cards/${jcId}/stage-flow`);
      setStageFlow(res.data);
    } catch {
      // a job card with no movements yet — the panel stays hidden
    }
  }, [jobCard?.id, jobCardId, isAdmin]);

  useEffect(() => { loadStageFlow(); }, [loadStageFlow]);

  // The ledger rides on the process rows rather than in a panel of its own: a
  // process is one row, and what it received and sent on belongs on that row.
  const showFlow = isAdmin && (stageFlow?.stages?.length || 0) > 0;

  // Where each batch of this job stands, and the one step either side of it —
  // the same move the order screen offers, read off the same pipeline.
  const stageMoves = useMemo(() => {
    const stages = jobCard?.production_stages || [];
    return (jobCard?.batches || [])
      .filter(batch => batch.status !== 'completed')
      .map(batch => {
        const index = batch.current_stage_index || 0;
        return {
          batchId: batch.id,
          batchNumber: batch.batch_number,
          quantity: batch.quantity,
          atStage: batch.current_stage_name || stages[index]?.stage_name || '',
          prevStage: index > 0 ? stages[index - 1]?.stage_name : null,
          nextStage: index < stages.length - 1 ? stages[index + 1]?.stage_name : null,
        };
      });
  }, [jobCard]);

  const afterMove = () => Promise.all([loadJobCard(), loadProcessTable(), loadStageFlow()]);

  const moveForward = async (move) => {
    setMoveBusy(true);
    try {
      const res = await api.post(`/job-cards/${jcId}/move-forward`, { batch_id: move.batchId });
      toast.success(res.data?.message || `Moved to ${move.nextStage}`);
      await afterMove();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot move forward');
    }
    setMoveBusy(false);
  };

  const moveBackward = async () => {
    if (!moveBackTarget) return;
    setMoveBusy(true);
    try {
      const res = await api.post(`/job-cards/${jcId}/move-backward`, {
        batch_id: moveBackTarget.batchId,
        reason: moveBackReason || undefined,
      });
      toast.success(res.data?.message || `Moved back to ${moveBackTarget.prevStage}`);
      setMoveBackTarget(null);
      setMoveBackReason('');
      await afterMove();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot move backward');
    }
    setMoveBusy(false);
  };

  const processRows = useMemo(() => {
    if (!showFlow) return processTable.rows;
    const byKey = new Map(stageFlow.stages.map(s => [s.stage_key || s.stage_name, s]));
    return processTable.rows.map(row => {
      const flow = byKey.get(row.stage_key) || byKey.get(row.process);
      return flow
        ? { ...row, qty_received: flow.received, qty_moved_on: flow.moved_on,
            qty_short: flow.short, movements: flow.movements }
        : row;
    });
  }, [showFlow, stageFlow, processTable.rows]);

  // One write for anything typed or picked in a process row. It goes to the job
  // card rather than a batch, so a press can be booked before the work reaches
  // the stage — the old path needed a batch already sitting on that row, which
  // is why most cells were dead.
  const saveProcessRow = async (row, patch, message) => {
    const jcId = jobCard?.id || jobCardId;
    try {
      const res = await api.patch('/production/runs/process-row', {
        job_card_id: jcId, stage_key: row.stage_key, ...patch,
      });
      setProcessTable(prev => ({
        ...prev,
        totals: res.data.totals || prev.totals,
        rows: prev.rows.map(r => (r.stage_key === row.stage_key
          ? { ...r, ...res.data.row, machine_options: r.machine_options, operator_options: r.operator_options }
          : r)),
      }));
      if (message) toast.success(message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save that');
      loadProcessTable();
    }
  };

  const assignMachine = (row, machine) => saveProcessRow(
    row, { machine_id: machine.id, machine_name: machine.machine_name },
    `${machine.machine_name} booked for ${row.process}`,
  );

  const assignOperator = (row, person) => saveProcessRow(
    row, { operator_id: person.id, operator: person.name },
    `${person.name} on ${row.process}`,
  );

  // Taking a press off a process, without touching where the work stands.
  const clearMachine = (row) => saveProcessRow(
    row, { clear_machine: true }, `Machine taken off ${row.process}`,
  );

  const editRow = (row, field, value) => saveProcessRow(row, { [field]: value });

  // The Design row picks a person, so it needs the designers rather than the
  // presses. The directory is the one list every role on this screen may read.
  useEffect(() => {
    if (!canEditPipeline) return;
    const loadDesigners = async () => {
      try {
        const { users = [] } = await searchUsers('', 100);
        setDesigners(
          users
            .filter(u => (u.roles || [u.role]).includes('designer'))
            .map(u => ({ id: u.id, name: u.full_name }))
        );
      } catch {
        // the Design cell falls back to plain text when nobody can be listed
      }
    };
    loadDesigners();
  }, [canEditPipeline]);

  // Fetch real-time pipeline preview when specs change
  useEffect(() => {
    const fetchPipelinePreview = async () => {
      if (!jobCard?.product_type_code) return;
      try {
        const res = await api.post(`/job-cards/pipeline-preview/${jobCard.product_type_code}`, {
          field_values: specs
        });
        setPipelinePreview({ stages: res.data.stages || [] });
      } catch {
        // Fall back to stored production_stages if API fails
        setPipelinePreview({
          stages: (jobCard?.production_stages || []).map((s, i) => ({ ...s, sequence: i })),
        });
      }
    };
    
    // Debounce the preview update
    const timeoutId = setTimeout(fetchPipelinePreview, 300);
    return () => clearTimeout(timeoutId);
  }, [specs, jobCard?.product_type_code, jobCard?.production_stages]);

  const handleSplitIntoTypes = async () => {
    const validTypes = newTypes.filter(t => t.name.trim() && parseInt(t.qty) > 0);
    if (validTypes.length < 2) {
      toast.error('At least 2 types with names and quantities required');
      return;
    }
    setSplitting(true);
    try {
      const res = await api.post(`/job-cards/${jobCard.id}/split-into-types`, {
        types: validTypes.map(t => ({ name: t.name.trim(), qty: parseInt(t.qty) })),
      });
      toast.success(res.data.message);
      setSplitDialogOpen(false);
      setNewTypes([{ name: '', qty: '' }, { name: '', qty: '' }]);
      loadJobCard();
      loadSplitData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to split');
    }
    setSplitting(false);
  };

  const handleAddType = async () => {
    if (!addTypeName.trim() || !parseInt(addTypeQty)) {
      toast.error('Name and quantity required');
      return;
    }
    setAddingType(true);
    try {
      const res = await api.post(`/job-cards/${jobCard.id}/add-type`, {
        name: addTypeName.trim(),
        qty: parseInt(addTypeQty),
      });
      toast.success(res.data.message);
      setAddTypeDialogOpen(false);
      setAddTypeName('');
      setAddTypeQty('');
      loadJobCard();
      loadSplitData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add type');
    }
    setAddingType(false);
  };

  const handleDeleteType = (childJcId, typeName) => setTypeToDelete({ childJcId, typeName });

  const confirmDeleteType = async () => {
    const target = typeToDelete;
    setTypeToDelete(null);
    if (!target) return;
    const { childJcId, typeName } = target;
    try {
      await api.delete(`/job-cards/${childJcId}/split-type`);
      toast.success(`Type "${typeName}" deleted`);
      loadJobCard();
      loadSplitData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete type');
    }
  };

  // Track changes
  useEffect(() => {
    if (!initialSpecs) return;
    const changed = JSON.stringify(specs) !== JSON.stringify(initialSpecs);
    setHasChanges(changed);
  }, [specs, initialSpecs]);

  // Update a spec field
  const updateSpec = (fieldName, value) => {
    setSpecs(prev => ({ ...prev, [fieldName]: value }));
  };

  // Save changes
  const handleSave = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      const jcId = jobCard?.id || jobCardId;
      const res = await api.put(`/job-cards/${jcId}`, { specifications: specs });
      setJobCard(res.data);
      setSpecs(res.data.specifications || {});
      setInitialSpecs(res.data.specifications || {});
      setFieldDefs(res.data.product_config?.job_card_fields || []);
      setProductConfig(res.data.product_config || null);
      setHasChanges(false);
      toast.success('Job card saved — stages updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save job card');
    }
    setSaving(false);
  };

  // Ply change handler with confirmation
  // Compute live pipeline from real-time preview or fallback to stored stages
  const liveStages = pipelinePreview.stages.length > 0
    ? pipelinePreview.stages
    : jobCard?.production_stages || [];

  // Processes this product type runs that the job does not have yet. Only these
  // can be added: a stage outside the registry has no supervisor linked to it.
  const addableStages = useMemo(() => {
    const onJob = new Set((processTable.rows || []).map(r => r.stage_key));
    return (productConfig?.stage_generation_rules || [])
      .filter(rule => rule.stage_key && !onJob.has(rule.stage_key))
      .filter(rule => !['packing', 'dispatch', 'stock_in'].includes(rule.stage_key));
  }, [productConfig, processTable.rows]);

  // Fields the stage rules read. A blank one of these can silently drop a
  // process, which is the only kind of blank worth chasing.
  const routingFields = useMemo(() => {
    const rules = productConfig?.stage_generation_rules || [];
    return new Set(
      rules.flatMap(rule => (rule.conditions || []).map(cond => cond.field)).filter(Boolean)
    );
  }, [productConfig]);

  // What is actually outstanding: required fields left blank, and routing
  // fields left blank. Everything else is filled in later by whoever knows the
  // answer, so counting it as incomplete only manufactures anxiety.
  const outstanding = useMemo(() => {
    const isBlank = (name) => {
      const val = specs[name];
      return val === undefined || val === null || val === '' ||
        ['none', 'n/a'].includes(String(val).toLowerCase()) ||
        (Array.isArray(val) && val.length === 0);
    };
    const visible = fieldDefs.filter(f => isPlyFieldVisible(f, specs));
    return {
      required: visible.filter(f => f.required && isBlank(f.name)),
      routing: visible.filter(f => routingFields.has(f.name) && isBlank(f.name)),
      blanks: visible.filter(f => isBlank(f.name)).length,
      total: visible.length,
    };
  }, [fieldDefs, specs, routingFields]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (!jobCard) {
    return (
      <div className="text-center py-12 text-slate-500">
        <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
        <p className="font-medium">Job card not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Go Back
        </Button>
      </div>
    );
  }

  const ji = jobCard.job_info || {};

  // thirteen columns is a sideways scroll on a handset; the same rows, cells
  // and locks read as one card per process there
  const Processes = isMobile ? PhoneProcessCards : ProcessTable;

  return (
    <Screen data-testid="job-card-page">
      {/* Header */}
      {isMobile ? (
        <PhoneJobCardHeader
          jobCard={jobCard}
          info={ji}
          splitEligibility={splitEligibility}
          splitTypes={splitTypes}
          canPrint={canPrintJobCard(user)}
          canFill={canFillCard}
          requiredMissing={outstanding.required.length}
          hasChanges={hasChanges}
          saving={saving}
          onBack={() => navigate(-1)}
          onPrint={handlePrintJobCard}
          onQr={() => downloadJobCardQr(jobCard.id, jobCard.job_card_id || jobCard.order_display_id)}
          onSpecs={() => navigate(`/job-card/${jcId}/fill`)}
          onSave={handleSave}
          onOpenParent={() => navigate(`/job-card/${splitEligibility?.parent_job_card_id}`)}
        />
      ) : (
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-xl font-bold text-slate-900">
                  {jobCard.is_standalone ? jobCard.standalone_name || 'Standalone Job Card' : 'Job Card'}
                </h1>
                <Badge className="text-xs bg-brand-blue/10 text-brand-blue border-0">
                  {DEPT_NAMES[jobCard.product_type_code] || jobCard.product_type_name}
                </Badge>
                {(jobCard.item_name || jobCard.product_subtype) && (
                  <Badge variant="outline" className="text-xs font-medium text-slate-900">{jobCard.item_name || jobCard.product_subtype}</Badge>
                )}
                {jobCard.type_name && (
                  <Badge className="text-xs bg-purple-50 text-purple-700 border-purple-200">{jobCard.type_name}</Badge>
                )}
                {jobCard.is_standalone && (user?.role === 'admin' || user?.role === 'supervisor') && (
                  <Badge className="text-xs bg-teal-50 text-teal-700 border-teal-200">Standalone</Badge>
                )}
                {splitEligibility?.already_split && (
                  <Badge className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                    <Scissors className="h-3 w-3 mr-1" /> Split into {splitTypes.length} types
                  </Badge>
                )}
                {splitEligibility?.is_child && (
                  <Badge className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200 cursor-pointer"
                    onClick={() => navigate(`/job-card/${splitEligibility.parent_job_card_id}`)}>
                    <ExternalLink className="h-3 w-3 mr-1" /> View Parent
                  </Badge>
                )}
                <Badge className={`text-xs ${(STATUS_COLORS[jobCard.status] || STATUS_COLORS.draft).class}`}>
                  {jobCard.status === 'in_progress' ? 'In Progress' : jobCard.status?.charAt(0).toUpperCase() + jobCard.status?.slice(1)}
                </Badge>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {jobCard.is_standalone
                  ? <>{jobCard.standalone_name || 'Standalone Job Card'} &middot; ID: {jobCard.id?.slice(0, 8)}</>
                  : <>Order {jobCard.order_display_id} &middot; Qty: {ji.quantity?.toLocaleString()}</>
                }
                {ji.priority && (
                  <span className={`ml-2 text-xs font-medium ${
                    ji.priority === 'high' ? 'text-red-600' : ji.priority === 'medium' ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    {ji.priority.toUpperCase()} PRIORITY
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
          {canPrintJobCard(user) && (
            <Button variant="outline" size="sm" data-testid="print-job-card-btn"
              onClick={handlePrintJobCard}>
              <Printer className="h-4 w-4 mr-1" /> Print Job Card
            </Button>
          )}
          <Button variant="outline" size="sm" data-testid="download-qr-btn"
            onClick={() => downloadJobCardQr(jobCard.id, jobCard.job_card_id || jobCard.order_display_id)}>
            <QrCode className="h-4 w-4 mr-1" /> QR Sticker
          </Button>
          {/* The specifications are edited on the fill screen — the same three
              panes the card was filled in on, where a spec sits beside the process
              it decides. This used to open a second, different editor in a drawer
              over the process table. */}
          {canFillCard && (
            <Button variant="outline" size="sm" data-testid="open-specs-btn"
              onClick={() => navigate(`/job-card/${jcId}/fill`)}>
              <Settings2 className="h-4 w-4 mr-1" />
              Specifications
              {outstanding.required.length > 0 && (
                <span className="ml-1.5 rounded-full bg-brand-orange px-1.5 text-[10px] text-white tabular-nums">
                  {outstanding.required.length}
                </span>
              )}
            </Button>
          )}
          {/* Save Button - prominent when changes exist */}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`${hasChanges
              ? 'bg-brand-blue hover:bg-brand-blue-dark text-white shadow-button-brand'
              : 'bg-slate-100 text-slate-400 cursor-not-allowed'} transition-all`}
            data-testid="save-btn"
          >
            {saving ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'All Saved'}
          </Button>
          </div>
        </div>
      )}

      {/* Split Into Types Section */}
      {splitEligibility?.eligible && !splitEligibility?.already_split && !splitEligibility?.is_child && (
        <Card className="border-blue-100 shadow-card rounded-xl bg-blue-50/30">
          <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Scissors className="h-4 w-4 text-blue-600" /> Split Into Types
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                This product type supports splitting into multiple types (e.g., different label designs).
                Each type gets its own job card and tracks independently.
              </p>
            </div>
            <Button
              onClick={() => setSplitDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm"
              data-testid="split-into-types-btn"
            >
              <Scissors className="h-4 w-4 mr-1" /> Split Into Types
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Split Types List — shown for parent */}
      {splitEligibility?.already_split && splitTypes.length > 0 && (
        <Card className="border-orange-100 shadow-card rounded-xl">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
              <Scissors className="h-3.5 w-3.5" /> Split Types ({splitTypes.length})
            </CardTitle>
            <Button
              size="sm"
              onClick={() => setAddTypeDialogOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8"
            >
              <Plus className="h-3 w-3 mr-1" /> Add Type
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {splitTypes.map(t => (
                <div key={t.job_card_id} className="flex flex-wrap items-center justify-between gap-2 p-3 bg-white border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                      t.is_completed ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {t.type_name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{t.type_name}</p>
                      <p className="text-xs text-slate-500">
                        Qty: {t.qty?.toLocaleString()}
                        {showFinancials && t.price_per_piece > 0 && ` • ₹${t.price_per_piece}/pc`}
                        {showFinancials && t.item_value > 0 && ` • Total: ₹${t.item_value.toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] ${
                      t.is_completed ? 'bg-green-50 text-green-700' :
                      t.current_stage ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {t.is_completed ? 'Completed' : t.current_stage || 'Pending'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => navigate(`/job-card/${t.job_card_id}`)}
                    >
                      <ExternalLink className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleDeleteType(t.job_card_id, t.type_name)}
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Split Into Types Dialog */}
      <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Split Into Types</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Split this job card into multiple types. Each type gets its own job card with independent production tracking.
              Base specs are cloned from this job card.
            </p>
            <div className="space-y-3">
              {newTypes.map((t, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div className="flex-1">
                    <Input
                      type="text"
                      className="h-9 text-sm"
                      placeholder={`Type ${idx + 1} name (e.g., Pesticide)`}
                      value={t.name}
                      onChange={e => setNewTypes(prev => prev.map((p, i) => i === idx ? { ...p, name: e.target.value } : p))}
                    />
                  </div>
                  <div className="w-28">
                    <Input
                      type="number"
                      className="h-9 text-sm"
                      placeholder="Qty"
                      value={t.qty}
                      onChange={e => setNewTypes(prev => prev.map((p, i) => i === idx ? { ...p, qty: e.target.value } : p))}
                    />
                  </div>
                  {newTypes.length > 2 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0"
                      onClick={() => setNewTypes(prev => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-4 w-4 text-red-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setNewTypes(prev => [...prev, { name: '', qty: '' }])}
            >
              <Plus className="h-4 w-4 mr-1" /> Add Another Type
            </Button>
            <div className="text-xs text-slate-400">
              Quantities are independent — no enforcement against original total.
              Price defaults to same per-piece rate and can be overridden later.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSplitIntoTypes}
              disabled={splitting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {splitting ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Scissors className="h-4 w-4 mr-1" />}
              {splitting ? 'Splitting...' : 'Split'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Type Dialog */}
      <Dialog open={addTypeDialogOpen} onOpenChange={setAddTypeDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-medium">Type Name</Label>
              <Input
                type="text"
                className="h-9 text-sm"
                placeholder="e.g., Fertilizer"
                value={addTypeName}
                onChange={e => setAddTypeName(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Quantity</Label>
              <Input
                type="number"
                className="h-9 text-sm"
                placeholder="e.g., 3000"
                value={addTypeQty}
                onChange={e => setAddTypeQty(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTypeDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddType}
              disabled={addingType}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {addingType ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              {addingType ? 'Adding...' : 'Add Type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* The job card as the floor reads it: one screen, the process table
          taking whatever height is left. */}
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {/* The job card as the floor reads it: the order line, the job's own
            facts, then every process across one table. */}
        <JobHeaderBand
          info={processTable.job_info}
          quantity={ji.quantity}
          orderId={jobCard?.order_display_id}
          specs={specs}
          onChange={updateSpec}
          canEdit={canEditPipeline}
        />

        {/* on a phone each batch is a line with its two moves as thumb-sized
            buttons, rather than a strip of small ones that wraps */}
        {canMoveStages && stageMoves.length > 0 && isMobile && (
          <PhoneCard title="Production stage" testID="stage-move-band">
            {stageMoves.map(move => (
              <div key={move.batchId} className="border-b py-2 last:border-b-0 last:pb-0" style={{ borderColor: Colors.border.subtle }}>
                <p className="text-[13px]" style={{ color: Colors.text.secondary }}>
                  {stageMoves.length > 1 && (
                    <span className="font-semibold" style={{ color: Colors.text.primary }}>
                      #{move.batchNumber} · {move.quantity?.toLocaleString()} pcs ·{' '}
                    </span>
                  )}
                  at <span className="font-semibold" style={{ color: Colors.text.primary }}>{move.atStage || '—'}</span>
                </p>
                <ActionRow
                  actions={[
                    move.prevStage && {
                      key: 'back', label: `Back to ${move.prevStage}`, icon: ChevronUp, tone: 'caution',
                      disabled: moveBusy, testID: `move-back-${move.batchId}`,
                      onClick: () => { setMoveBackTarget(move); setMoveBackReason(''); },
                    },
                    move.nextStage && {
                      key: 'forward', label: `Move to ${move.nextStage}`, icon: ChevronDown, tone: 'positive',
                      disabled: moveBusy, testID: `move-forward-${move.batchId}`,
                      onClick: () => moveForward(move),
                    },
                  ]}
                />
              </div>
            ))}
          </PhoneCard>
        )}

        {canMoveStages && stageMoves.length > 0 && !isMobile && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border border-slate-300 bg-slate-50 px-2 py-1.5 text-xs"
            data-testid="stage-move-band">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Production Stage</span>
            {stageMoves.map(move => (
              <div key={move.batchId} className="flex items-center gap-2">
                {stageMoves.length > 1 && (
                  <span className="font-semibold text-slate-700">
                    #{move.batchNumber} <span className="font-normal text-slate-400">{move.quantity?.toLocaleString()} pcs</span>
                  </span>
                )}
                <span className="text-slate-400">at</span>
                <span className="font-medium text-slate-800">{move.atStage || '—'}</span>
                {move.prevStage && (
                  <button type="button" disabled={moveBusy}
                    onClick={() => { setMoveBackTarget(move); setMoveBackReason(''); }}
                    className="flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-40"
                    data-testid={`move-back-${move.batchId}`}>
                    <ChevronUp className="h-3.5 w-3.5" /> Back to {move.prevStage}
                  </button>
                )}
                {move.nextStage && (
                  <button type="button" disabled={moveBusy}
                    onClick={() => moveForward(move)}
                    className="flex items-center gap-1 rounded-md border border-green-300 bg-green-50 px-2.5 py-1 font-medium text-green-700 hover:bg-green-100 disabled:opacity-40"
                    data-testid={`move-forward-${move.batchId}`}>
                    Move to {move.nextStage} <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <Processes
          rows={processRows}
          totals={processTable.totals}
          onAssignMachine={assignMachine}
          onAssignOperator={assignOperator}
          onClearMachine={clearMachine}
          designerOptions={designers}
          onEditRow={editRow}
          canEdit={canEditPipeline}
          onMove={moveStage}
          onRemove={removeStage}
          onAdd={addStage}
          addableStages={addableStages}
          busy={pipelineBusy}
          showFlow={showFlow}
        />

        {/* Going back is the move that needs a reason on it — the same question
            the order screen asks before it sends work back a stage. */}
        <Dialog open={!!moveBackTarget}
          onOpenChange={(open) => { if (!open) { setMoveBackTarget(null); setMoveBackReason(''); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Move Job Card Backward</DialogTitle>
              <DialogDescription>
                Send this job card back to <strong>{moveBackTarget?.prevStage}</strong>
                {moveBackTarget?.atStage && <span> — it is at {moveBackTarget.atStage} now.</span>}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label className="text-xs font-medium">Reason (optional)</Label>
              <Textarea
                value={moveBackReason}
                onChange={e => setMoveBackReason(e.target.value)}
                placeholder="E.g., quality issue requires rework..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMoveBackTarget(null)}>Cancel</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white"
                disabled={moveBusy} onClick={moveBackward} data-testid="confirm-move-back">
                {moveBusy ? 'Moving...' : `Move back to ${moveBackTarget?.prevStage}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!typeToDelete} onOpenChange={() => setTypeToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{`Delete type “${typeToDelete?.typeName}”?`}</AlertDialogTitle>
            <AlertDialogDescription>Its job card goes with it. Work already recorded against it is removed too.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteType} className="bg-red-600 text-white hover:bg-red-700">
              Delete type
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      </div>

      {/* Sticky Save Bar */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-slate-200 shadow-elevated px-4 py-3 md:px-6">
          <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              <span className="font-medium text-brand-orange">Unsaved changes</span> — Save to update the production pipeline
            </p>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-brand-blue hover:bg-brand-blue-dark text-white shadow-button-brand"
            >
              {saving ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}
    </Screen>
  );
}

function InfoCell({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-slate-900 mt-0.5">{value || '-'}</p>
    </div>
  );
}
